import _ from 'lodash';
import {
    CustomersController,
    ICustomersGetResponse,
    ICustomersInsuranceManualResponse,
    ICustomersInsuranceTestClaim,
    TestInsuranceResponseStatus
} from '../endpoints/customers';
import { ITransferPrescriptions } from '../types/interfaces/transfer-prescriptions';
import {
    getPreviousTestClaim,
    testClaim,
    updateTestClaimMetadataModel,
    findPrevTestClaimWithOnlyErrors,
    isProductServiceNotCovered
} from './test-claim';
import { store } from '@/store';
import { ICustomersUserOnboardingResponse } from '../endpoints/customers';
import { filterSupportedInsurances } from './insurance';

const paidStatuses = new Set<TestInsuranceResponseStatus>(['PAID', 'DUPLICATE_OF_PAID', 'APPROVED']);
const rejectCodesToHalt = new Set([
    'PHARMACY_NOT_CONTRACTED_WITH_PLAN_PROCESSOR_ON_DATE_OF_SERVICE',
    'PHARMACY_NOT_CONTRACTED_IN_ASSISTED_LIVING_NETWORK',
    'PHARMACY_NOT_CONTRACTED_IN_RETAIL_NETWORK',
    'PHARMACY_NOT_CONTRACTED_IN_MAIL_ORDER_NETWORK',
    'PHARMACY_NOT_CONTRACTED_IN_HOSPICE_NETWORK',
    'PHARMACY_NOT_CONTRACTED_IN_VETERANS_ADMINISTRATION_NETWORK',
    'PHARMACY_NOT_CONTRACTED_IN_MILITARY_NETWORK',
    'PHARMACY_NOT_CONTRACTED_IN_SPECIALTY_NETWORK',
    'PHARMACY_NOT_CONTRACTED_IN_HOME_INFUSION_NETWORK',
    'PHARMACY_NOT_CONTRACTED_IN_LONG_TERM_CARE_NETWORK',
    'PHARMACY_NOT_CONTRACTED_IN_90_DAY_RETAIL_NETWORK',
    'NON_MATCHED_PHARMACY_NUMBER',
    'PHARMACY_NOT_ENROLLED_IN_STATE_MEDICAID_PROGRAM',
    'MISSING_SERVICE_PROVIDER_NUMBER'
]);

export const getCopayEligiblePrescriptions = (prescriptions?: ITransferPrescriptions[]) => {
    return prescriptions?.filter(item => !item.otc) || [];
};

export const isInsuranceEligible = (claim?: ICustomersInsuranceTestClaim) =>
    !!claim && claim.prescriptions.some(item => paidStatuses.has(item.responseStatus));

export const insuranceRejectErrorsDoesNotHalt = (claim?: ICustomersInsuranceTestClaim) =>
    !!claim &&
    claim.prescriptions.some(item => {
        return item.rejectCodes && !item.rejectCodes.some(code => rejectCodesToHalt.has(code)) && !isProductServiceNotCovered(item);
    });

const suspectedIncorrectInsuranceInputCodes = new Set([
    'NON_MATCHED_CARDHOLDER_ID',
    'MISSING_GROUP_ID',
    'MISSING_BIN_NUMBER',
    'MISSING_DATE_OF_BIRTH'
]);

export const hasSuspectedIncorrectInsuranceInputs = (claim?: ICustomersInsuranceTestClaim) =>
    !!claim &&
    claim.prescriptions.some(
        item => !!item.rejectCodes?.some(code => suspectedIncorrectInsuranceInputCodes.has(code))
    );



    
export const findAllEligibleInsurance = async (
    _insurances: ICustomersInsuranceManualResponse[],
    prescriptions: ITransferPrescriptions[],
    customer?: ICustomersGetResponse
) => {
    
    const insurances = filterSupportedInsurances(_insurances);
    const checkPrescription = getCopayEligiblePrescriptions(prescriptions)[0];

    if (!checkPrescription)
        return {
            newClaims: [],
            eligibleInsurances: [],
            eligibilityPassed: true
        };

    const requests = insurances.map(async insurance => {
        const previousClaim = getPreviousTestClaim(insurance, customer);
        return previousClaim && previousClaim.prescriptions.every(prescription => !prescription.error)
            ? { hasUpdate: false, result: getPreviousTestClaim(insurance, customer) }
            : testClaim(insurance, [checkPrescription], customer);
    });
    const results = await Promise.allSettled(requests);

    const [fulfilled] = _.partition(results, item => item.status === 'fulfilled');

    const newClaims = fulfilled
        .filter(item => item.value.hasUpdate)
        .map(item => item.value.result!)
        .filter(Boolean);

    const eligibleInsurances = fulfilled
        .filter(item => isInsuranceEligible(item.value.result) || insuranceRejectErrorsDoesNotHalt(item.value.result))
        .map(item => insurances.find(ins => ins.id === item.value.result!.insuranceId)!)
        .filter(Boolean);

    const insuranceClaimsWithFailedEligibilityCheck = fulfilled.filter(item => {
        return item.value.result?.prescriptions.some(prescription => !!prescription.error);
    });

    // Consumer should care about updating metadata on its own
    // If newClaims list is not empty
    let eligibilityPassed = true;
    const cust = customer || store.getState().onboarding.user;
    if (cust?.insuranceUploads.length) {
        eligibilityPassed = true;
    } else if (fulfilled.length) {
        eligibilityPassed = !!eligibleInsurances.length || !!insuranceClaimsWithFailedEligibilityCheck.length;
    }

    return {
        newClaims,
        eligibleInsurances,
        eligibilityPassed,
        eligibilityNeedsRetry: !!insuranceClaimsWithFailedEligibilityCheck.length
    };
};

export const checkIfNoneOfInsurancesEligible = async (test?: string) => {
    console.log(test);
    const user = store.getState().onboarding.user;
    if (!user?.insurances.length || !user?.transferPrescriptions?.length) return true;

    const { eligibilityPassed } = await findAllEligibleInsurance(user.insurances, user.transferPrescriptions);
    // this may also run a request but it will never persist it ..... not sure about this
    return eligibilityPassed;
};

export const pendingEligibilityChecks: Promise<boolean | null>[] = [];

const pureCheckEligibilityAndSave = async ({
    insuranceList,
    prescriptionList,
    customer,
    additionalMetaData,
    forceMetadataUpdate
}: {
    insuranceList?: ICustomersInsuranceManualResponse[];
    prescriptionList?: ITransferPrescriptions[];
    customer?: ICustomersGetResponse;
    additionalMetaData?: Partial<ICustomersUserOnboardingResponse>;
    forceMetadataUpdate?: boolean;
} = {}): Promise<boolean | null> => {
    // await new Promise(resolve => setTimeout(resolve, 10000)); //test
    const user = customer || store.getState().onboarding.user;
    
    if (!user?.userOnboarding) return null;
    const { newClaims, eligibilityPassed } = await findAllEligibleInsurance(
        insuranceList || user.insurances,
        prescriptionList || user.transferPrescriptions,
        customer
    );
    if (!!newClaims.length || (forceMetadataUpdate && !_.isEmpty(additionalMetaData || {}))) {
        await store.dispatch(
            CustomersController.updateMetadata(updateTestClaimMetadataModel(user.userOnboarding, newClaims, additionalMetaData))
        );
    }
    return eligibilityPassed;
};

const safeCheckEligibilityAndSave = async (option: Parameters<typeof pureCheckEligibilityAndSave>[0]) => {
    if (!pendingEligibilityChecks.length) return pureCheckEligibilityAndSave(option);

    await pendingEligibilityChecks[pendingEligibilityChecks.length - 1];
    await new Promise(resolve => setTimeout(resolve, 0));

    return pureCheckEligibilityAndSave({ additionalMetaData: option?.additionalMetaData });
};

export const checkEligibilityAndSave = async (option: Parameters<typeof pureCheckEligibilityAndSave>[0]) => {
    
    const promise = safeCheckEligibilityAndSave(option);

    pendingEligibilityChecks.push(promise);

    const result = await promise;

    const ownIndex = pendingEligibilityChecks.indexOf(promise);
    pendingEligibilityChecks.splice(ownIndex, 1);

    return result;
};

export const checkHasPendingEligibilityTest = (user: ICustomersGetResponse) => {
    if (!user?.userOnboarding) return false;

    const checkPrescription = getCopayEligiblePrescriptions(user.transferPrescriptions)[0];
    const notCheckedInsurances = user.insurances.filter(
        item => !getPreviousTestClaim(item, user) || findPrevTestClaimWithOnlyErrors(item, user)
    );
    return !!checkPrescription && !!notCheckedInsurances.length;
};

export const getPendingEligibilityChecks = () => {
    const { user } = store.getState().onboarding;

    if (!user?.userOnboarding || !user?.insurances) return _.noop;

    const checkPrescription = getCopayEligiblePrescriptions(user.transferPrescriptions)[0];
    const uncheckedInsurances = user.insurances.filter(item => !getPreviousTestClaim(item));

    if (!checkPrescription || !uncheckedInsurances.length) return _.noop;
    
    return () =>
        checkEligibilityAndSave({
            insuranceList: uncheckedInsurances,
            prescriptionList: [checkPrescription]
        });
};
